import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRightIcon, MapPinIcon, CalendarIcon, UsersIcon, CheckCircleIcon, TagIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { regionDatabase, ClassSession, Venue } from '../data/bookingData';
export function EventBookingPage() {
  const {
    region,
    sessionId
  } = useParams<{
    region: string;
    sessionId: string;
  }>();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<'both' | 'single' | ''>('');
  const [selectedDay, setSelectedDay] = useState<'day1' | 'day2' | ''>('');
  const regionKey = region?.toLowerCase() || '';
  const data = regionDatabase[regionKey];
  let session: ClassSession | undefined;
  let venue: Venue | undefined;
  if (data) {
    for (const v of data.venues) {
      const foundSession = v.sessions.find(s => s.id === sessionId);
      if (foundSession) {
        session = foundSession;
        venue = v;
        break;
      }
    }
  }
  if (!data || !session || !venue) {
    return <main className="min-h-[70vh] flex items-center justify-center bg-navy-50 px-4 pt-20" data-id="element-717">
        <div className="text-center max-w-lg" data-id="element-718">
          <h1 className="text-4xl font-bold text-navy-900 mb-4" data-id="element-719">
            Event Not Found
          </h1>
          <p className="text-lg text-navy-600 mb-8" data-id="element-720">
            We couldn't find the booking information for this event.
          </p>
          <Link to={`/book/${regionKey || ''}`} data-id="element-721">
            <Button variant="primary" className="inline-flex items-center" data-id="element-722">
              <ArrowRightIcon className="w-4 h-4 mr-2 rotate-180" data-id="element-723" />
              Back to Classes
            </Button>
          </Link>
        </div>
      </main>;
  }
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    window.scrollTo(0, 0);
  };
  if (isSubmitted) {
    return <main className="bg-white pt-20 min-h-screen flex flex-col" data-id="element-724">
        <section className="flex-grow flex items-center justify-center py-24 px-4" data-id="element-725">
          <div className="max-w-xl mx-auto text-center" data-id="element-726">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8 text-green-600" data-id="element-727">
              <CheckCircleIcon className="w-10 h-10" data-id="element-728" />
            </div>
            <h1 className="text-4xl font-display font-bold text-navy-900 mb-6" data-id="element-729">
              Booking Request Submitted
            </h1>
            <p className="text-lg text-navy-600 mb-10 leading-relaxed" data-id="element-730">
              Thank you! We've received your booking request for{' '}
              <strong data-id="element-731">{session.title}</strong>. We'll be in touch shortly to
              confirm your place.
            </p>
            <Link to={`/book/${regionKey}`} data-id="element-732">
              <Button variant="primary" size="lg" data-id="element-733">
                Back to {data.name} Classes
              </Button>
            </Link>
          </div>
        </section>
      </main>;
  }
  return <main className="bg-white pt-20" data-id="element-734">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-brand-blue pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px]" data-id="element-735">
        <img src="https://cdn.magicpatterns.com/uploads/8m3ZDfLxFh7GqHfUAhVAku/Adults-Badminton-Photo-TK-LK-scaled-e1737371933423_(1).jpg" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-736" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-737"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-738">
          <nav aria-label="Breadcrumb" className="flex text-sm font-medium text-white/80 mb-8" data-id="element-739">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-740">
              Home
            </Link>
            <span className="mx-2" data-id="element-741">/</span>
            <Link to="/book" className="hover:text-white transition-colors" data-id="element-742">
              Book Now
            </Link>
            <span className="mx-2" data-id="element-743">/</span>
            <Link to={`/book/${regionKey}`} className="hover:text-white transition-colors" data-id="element-744">
              {data.name}
            </Link>
            <span className="mx-2" data-id="element-745">/</span>
            <span className="text-white" data-id="element-746">Booking</span>
          </nav>

          <div className="max-w-3xl" data-id="element-747">
            <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-brand-yellow" data-id="element-748">
              Booking Form
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6 leading-tight uppercase" data-id="element-749">
              Complete Your Booking
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed" data-id="element-750">
              Fill in the details below to book your place.
            </p>
          </div>
        </div>
      </section>

      {/* Event Details Block */}
      <section className="bg-navy-50 py-8 border-b border-navy-100" data-id="element-751">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-752">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6" data-id="element-753">
            <div data-id="element-754">
              <h2 className="text-lg font-bold text-navy-900 mb-4" data-id="element-755">
                Event Details
              </h2>
              <h3 className="text-xl font-display font-bold text-brand-blue mb-3" data-id="element-756">
                {session.sport} — {session.title}
              </h3>
              <div className="space-y-2 text-navy-700" data-id="element-757">
                <div className="flex items-start" data-id="element-758">
                  <MapPinIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0 mt-0.5" data-id="element-759" />
                  <span data-id="element-760">
                    <strong data-id="element-761">{venue.name}</strong>
                    <br data-id="element-762" />
                    {venue.address}
                  </span>
                </div>
                <div className="flex items-center" data-id="element-763">
                  <CalendarIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0" data-id="element-764" />
                  <span data-id="element-765">{session.dayTime}</span>
                </div>
                <div className="flex items-center" data-id="element-766">
                  <UsersIcon className="w-5 h-5 text-navy-400 mr-3 shrink-0" data-id="element-767" />
                  <span data-id="element-768">{session.ageGroup}</span>
                </div>
                {session.pricing && <div className="flex items-center font-semibold text-navy-900" data-id="element-769">
                    <TagIcon className="w-5 h-5 text-brand-yellow mr-3 shrink-0" data-id="element-770" />
                    <span data-id="element-771">{session.pricing}</span>
                  </div>}
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2" data-id="element-772">
              <Link to={`/book/${regionKey}`} className="text-sm font-medium text-brand-red hover:text-red-800 underline underline-offset-4 transition-colors" data-id="element-773">
                Change class
              </Link>
              <Link to="/book" className="text-sm font-medium text-navy-500 hover:text-navy-700 underline underline-offset-4 transition-colors" data-id="element-774">
                Change location
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-16 bg-white" data-id="element-775">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-776">
          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-777">
            {/* Section 1: Primary Contact */}
            <div className="space-y-4" data-id="element-778">
              <div className="border-b border-gray-200 pb-2" data-id="element-779">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-780">
                  Parent / Guardian — Primary Emergency Contact
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-781">
                <div className="md:col-span-2" data-id="element-782">
                  <label htmlFor="primaryName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-783">
                    Full Name *
                  </label>
                  <input type="text" id="primaryName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-784" />
                </div>
                <div data-id="element-785">
                  <label htmlFor="primaryPhone" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-786">
                    Phone Number *
                  </label>
                  <input type="tel" id="primaryPhone" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-787" />
                </div>
                <div data-id="element-788">
                  <label htmlFor="primaryEmail" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-789">
                    Email Address *
                  </label>
                  <input type="email" id="primaryEmail" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-790" />
                </div>
              </div>
            </div>

            {/* Section 2: Secondary Contact */}
            <div className="space-y-4" data-id="element-791">
              <div className="border-b border-gray-200 pb-2" data-id="element-792">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-793">
                  Parent / Guardian — Secondary Emergency Contact
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-794">
                <div data-id="element-795">
                  <label htmlFor="secondaryName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-796">
                    Full Name *
                  </label>
                  <input type="text" id="secondaryName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-797" />
                </div>
                <div data-id="element-798">
                  <label htmlFor="secondaryPhone" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-799">
                    Phone Number *
                  </label>
                  <input type="tel" id="secondaryPhone" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-800" />
                </div>
              </div>
            </div>

            {/* Section 3: Child Details */}
            <div className="space-y-4" data-id="element-801">
              <div className="border-b border-gray-200 pb-2" data-id="element-802">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-803">
                  Child's Details
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-804">
                <div data-id="element-805">
                  <label htmlFor="childName" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-806">
                    Full Name *
                  </label>
                  <input type="text" id="childName" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-807" />
                </div>
                <div data-id="element-808">
                  <label htmlFor="childDob" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-809">
                    Date of Birth *
                  </label>
                  <input type="date" id="childDob" required placeholder="dd/mm/yyyy" className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow" data-id="element-810" />
                </div>
              </div>
            </div>

            {/* Section 4: Package Selection & Additional Info */}
            <div className="space-y-4" data-id="element-811">
              <div className="border-b border-gray-200 pb-2" data-id="element-812">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-813">
                  Attendance &amp; Additional Information
                </h3>
              </div>

              {session.pricingOptions ? <div className="space-y-4" data-id="element-814">
                  <label className="block text-sm font-medium text-navy-900 mb-1" data-id="element-815">
                    Select Your Package *
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-816">
                    {/* Both Days Option */}
                    <label className={`relative flex flex-col p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedPackage === 'both' ? 'border-brand-blue bg-blue-50/50 ring-1 ring-brand-blue' : 'border-navy-200 hover:border-navy-300 bg-white'}`} data-id="element-817">
                      <input type="radio" name="package" value="both" required checked={selectedPackage === 'both'} onChange={() => {
                    setSelectedPackage('both');
                    setSelectedDay('');
                  }} className="sr-only" data-id="element-818" />
                      <div className="absolute -top-2.5 right-3" data-id="element-819">
                        <span className="inline-block bg-brand-green text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-820">
                          {session.pricingOptions.bothDaysSavings || 'Best Value'}
                        </span>
                      </div>
                      <div className="flex items-baseline justify-between mb-3" data-id="element-821">
                        <span className="text-lg font-bold text-navy-900" data-id="element-822">
                          Both Days
                        </span>
                        <span className="text-2xl font-bold text-brand-blue" data-id="element-823">
                          {session.pricingOptions.bothDaysPrice}
                        </span>
                      </div>
                      <div className="space-y-1.5 text-sm text-navy-600" data-id="element-824">
                        <div className="flex items-center" data-id="element-825">
                          <CheckCircleIcon className="w-4 h-4 text-brand-green mr-2 flex-shrink-0" data-id="element-826" />
                          <span data-id="element-827">{session.pricingOptions.day1Label}</span>
                        </div>
                        <div className="flex items-center" data-id="element-828">
                          <CheckCircleIcon className="w-4 h-4 text-brand-green mr-2 flex-shrink-0" data-id="element-829" />
                          <span data-id="element-830">{session.pricingOptions.day2Label}</span>
                        </div>
                      </div>
                    </label>

                    {/* Single Day Option */}
                    <label className={`relative flex flex-col p-5 rounded-xl border-2 cursor-pointer transition-all ${selectedPackage === 'single' ? 'border-brand-blue bg-blue-50/50 ring-1 ring-brand-blue' : 'border-navy-200 hover:border-navy-300 bg-white'}`} data-id="element-831">
                      <input type="radio" name="package" value="single" required checked={selectedPackage === 'single'} onChange={() => setSelectedPackage('single')} className="sr-only" data-id="element-832" />
                      <div className="flex items-baseline justify-between mb-3" data-id="element-833">
                        <span className="text-lg font-bold text-navy-900" data-id="element-834">
                          Single Day
                        </span>
                        <span className="text-2xl font-bold text-navy-700" data-id="element-835">
                          {session.pricingOptions.singleDayPrice}
                        </span>
                      </div>
                      <p className="text-sm text-navy-500" data-id="element-836">
                        Attend one day of the workshop
                      </p>
                    </label>
                  </div>

                  {/* Day selector when single day is chosen */}
                  {selectedPackage === 'single' && <div className="pl-1 pt-2 space-y-3" data-id="element-837">
                      <label className="block text-sm font-medium text-navy-900" data-id="element-838">
                        Which day would you like to attend? *
                      </label>
                      <div className="flex flex-col sm:flex-row gap-3" data-id="element-839">
                        <label className={`flex-1 flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedDay === 'day1' ? 'border-brand-blue bg-blue-50/50' : 'border-navy-200 hover:border-navy-300'}`} data-id="element-840">
                          <input type="radio" name="selectedDay" value="day1" required checked={selectedDay === 'day1'} onChange={() => setSelectedDay('day1')} className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue mr-3" data-id="element-841" />
                          <span className="text-sm font-medium text-navy-900" data-id="element-842">
                            {session.pricingOptions.day1Label}
                          </span>
                        </label>
                        <label className={`flex-1 flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedDay === 'day2' ? 'border-brand-blue bg-blue-50/50' : 'border-navy-200 hover:border-navy-300'}`} data-id="element-843">
                          <input type="radio" name="selectedDay" value="day2" required checked={selectedDay === 'day2'} onChange={() => setSelectedDay('day2')} className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue mr-3" data-id="element-844" />
                          <span className="text-sm font-medium text-navy-900" data-id="element-845">
                            {session.pricingOptions.day2Label}
                          </span>
                        </label>
                      </div>
                    </div>}
                </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-846">
                  <div data-id="element-847">
                    <label htmlFor="attendance" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-848">
                      Select Days Attending *
                    </label>
                    <select id="attendance" required className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow bg-white" data-id="element-849">
                      <option value="" data-id="element-850">Please select...</option>
                      <option value="both" data-id="element-851">Both Days</option>
                      <option value="day1" data-id="element-852">Day 1 Only</option>
                      <option value="day2" data-id="element-853">Day 2 Only</option>
                    </select>
                  </div>
                </div>}

              <div data-id="element-854">
                <label htmlFor="medicalInfo" className="block text-sm font-medium text-navy-900 mb-1.5" data-id="element-855">
                  Medical / SEN / Any Other Information
                </label>
                <textarea id="medicalInfo" rows={3} className="w-full border border-gray-300 rounded-md px-4 py-2.5 text-navy-900 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue outline-none transition-shadow resize-y" data-id="element-856"></textarea>
              </div>
            </div>

            {/* Section 5: Confirmations */}
            <div className="space-y-5" data-id="element-857">
              <div className="border-b border-gray-200 pb-2" data-id="element-858">
                <h3 className="text-lg font-display font-bold text-navy-900" data-id="element-859">
                  Confirmations
                </h3>
              </div>

              <div className="space-y-4" data-id="element-860">
                <div className="flex items-start" data-id="element-861">
                  <div className="flex items-center h-5 mt-0.5" data-id="element-862">
                    <input id="terms" type="checkbox" required className="w-4 h-4 border-gray-300 rounded text-brand-blue focus:ring-brand-blue" data-id="element-863" />
                  </div>
                  <div className="ml-3" data-id="element-864">
                    <label htmlFor="terms" className="text-sm font-medium text-navy-900" data-id="element-865">
                      I confirm I have read the{' '}
                      <Link to="/legal" state={{
                      tab: 'terms'
                    }} className="text-brand-blue hover:underline" data-id="element-866">
                        Terms and Conditions
                      </Link>{' '}
                      *
                    </label>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-867">
                  <div className="flex items-center h-5 mt-0.5" data-id="element-868">
                    <input id="privacy" type="checkbox" required className="w-4 h-4 border-gray-300 rounded text-brand-blue focus:ring-brand-blue" data-id="element-869" />
                  </div>
                  <div className="ml-3" data-id="element-870">
                    <label htmlFor="privacy" className="text-sm font-medium text-navy-900" data-id="element-871">
                      I confirm I have read the{' '}
                      <Link to="/legal" state={{
                      tab: 'privacy'
                    }} className="text-brand-blue hover:underline" data-id="element-872">
                        Privacy Policy
                      </Link>{' '}
                      *
                    </label>
                  </div>
                </div>

                <div className="pt-2" data-id="element-873">
                  <p className="text-sm font-medium text-navy-900 mb-1" data-id="element-874">
                    Photo / Video Permission *
                  </p>
                  <p className="text-xs text-navy-600 mb-3" data-id="element-875">
                    I give permission for my child's photo and video image to be
                    used on Bright Star Sports social media, website and print.{' '}
                    <Link to="/legal" state={{
                    tab: 'photo-consent'
                  }} className="text-brand-blue hover:underline" data-id="element-876">
                      More information
                    </Link>
                  </p>
                  <div className="flex items-center gap-6" data-id="element-877">
                    <div className="flex items-center" data-id="element-878">
                      <input id="photo-yes" name="photoPermission" type="radio" value="yes" required className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue" data-id="element-879" />
                      <label htmlFor="photo-yes" className="ml-2 text-sm text-navy-900" data-id="element-880">
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center" data-id="element-881">
                      <input id="photo-no" name="photoPermission" type="radio" value="no" required className="w-4 h-4 border-gray-300 text-brand-blue focus:ring-brand-blue" data-id="element-882" />
                      <label htmlFor="photo-no" className="ml-2 text-sm text-navy-900" data-id="element-883">
                        No
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200" data-id="element-884">
              <Button type="submit" variant="primary" size="lg" className="w-full md:w-auto" data-id="element-885">
                Submit Booking Request
              </Button>
            </div>
          </form>
        </div>
      </section>
    </main>;
}